<template>
    <filter-base
        @back="emit('back')"
        @reset="emit('reset')"
        @apply="emit('apply')"
    >
        <ion-accordion-group :value="filterValue">
            <ion-accordion value="sort">
                <ion-item
                    slot="header"
                    class="grey4-grey4-background purple-purple-color max-width accordion-90-deg filter-accordion-header grey-accordion-toggle"
                    lines="none"
                    @click.stop="changeAccordion('sort')"
                >
                    {{ $t('label.sort') }}
                </ion-item>

                <div slot="content" class="px-3 pb-3 pt-1">
                    <ion-radio-group
                        v-model="sortDirectionValue"
                        class="max-width"
                    >
                        <ion-row
                            v-for="option of sortOptions"
                            :key="option.value"
                            @click="sortDirectionValue = option.value"
                            class="cursor-pointer"
                        >
                            <ion-col class="center-y break-word">
                                <ion-radio
                                    mode="md"
                                    :value="option.value"
                                    class="filter-checkbox"
                                />
                                <ion-label class="ml-1 purple-purple-color fs-14">
                                    {{ option.text ? option.text : $t('label.noFilter') }}
                                </ion-label>
                            </ion-col>
                        </ion-row>

                        <div
                            v-if="/* isList && !persistentStore.offlineMode */ /* code from Ihor -> disable button */ false"
                            class="center-y" @click="switchToManualSorting"
                        >
                            <button-base
                                :color="ION_VARIANT.GREY_4"
                                class="round-button grey1-grey1-color inherit-color no-shadow"
                                style="width: 50px; height: 50px;"
                            >
                                <ion-icon src="assets/icon/pencil.svg" class="round-button-icon"/>
                            </button-base>
                            <ion-text class="ml-1 purple-purple-color">
                                {{ $t('button.manualSort') }}
                            </ion-text>
                        </div>
                    </ion-radio-group>

                    <template v-if="isList">
                        <ion-row class="mt-1">
                            <ion-col class="fs-smaller grey1-grey1-color">
                                {{ $t('label.quantity') }}
                            </ion-col>
                        </ion-row>

                        <ion-row>
                            <ion-col class="center-y" size="6">
                                <ion-checkbox
                                    mode="md"
                                    v-model="quantityModel"
                                    class="filter-checkbox"
                                />

                                <div
                                    class="ml-1 cursor-pointer purple-purple-color"
                                    @click="emit('update:withQuantity', !withQuantity)"
                                >
                                    {{ $t('label.quantityBiggerZero') }}
                                </div>
                            </ion-col>
                        </ion-row>
                    </template>
                </div>
            </ion-accordion>

            <ion-accordion v-if="isList && sortDirectionValue !== SORT_METHOD.MANUAL" value="tags">
                <ion-item
                    slot="header"
                    class="grey4-grey4-background purple-purple-color max-width filter-accordion-header accordion-90-deg grey-accordion-toggle"
                    lines="none"
                    @click.stop="changeAccordion('tags')"
                >
                    {{ $t('label.ownCategories') }}
                </ion-item>

                <div slot="content" class="pb-3 px-3 pt-1">
                    <ion-row v-if="ownCategories">
                        <template v-if="ownCategories.length > 0">
                            <template v-for="tag of ownCategories" :key="tag.id">
                                <ion-col class="center-y" size="6">
                                    <ion-checkbox
                                        mode="md"
                                        v-model="tagValues[tag.id]"
                                        class="filter-checkbox"
                                        style="min-width: 18px;"
                                    />

                                    <div
                                        class="ml-1 cursor-pointer purple-purple-color fs-14"
                                        @click="tagValues[tag.id] = !tagValues[tag.id]"
                                    >
                                        {{ tag.name }}
                                    </div>
                                </ion-col>
                            </template>
                        </template>

                        <ion-col v-else>
                            {{ $t('messages.list.noTags') }}
                        </ion-col>
                    </ion-row>
                </div>
            </ion-accordion>

            <ion-accordion
                v-if="sortDirectionValue !== SORT_METHOD.MANUAL && supplierStore.getSupplierCategoriesOptions(supplierId).length > 1"
                value="categories"
            >
                <ion-item
                    slot="header"
                    class="grey4-grey4-background purple-purple-color max-width filter-accordion-header accordion-90-deg grey-accordion-toggle"
                    lines="none"
                    @click.stop="changeAccordion('categories')"
                >
                    {{ $t('label.supplierCategories') }}
                </ion-item>

                <div slot="content" class="pb-3 px-3 pt-1">
                    <ion-row>
                        <template v-for="category of supplierStore.getSupplierCategoriesOptions(supplierId)" :key="category.id">
                            <ion-col class="center-y" size="6">
                                <ion-checkbox
                                    mode="md"
                                    v-model="catalogValues[category.id]"
                                    class="filter-checkbox"
                                    style="min-width: 18px;"
                                />

                                <div
                                    class="ml-1 cursor-pointer purple-purple-color fs-14"
                                    @click="catalogValues[category.id] = !catalogValues[category.id]"
                                >
                                    <template v-if="category.name">
                                        {{ category.name }}
                                    </template>
                                    <template v-else>{{ $t('label.uncategorized') }}</template>
                                </div>
                            </ion-col>
                        </template>
                    </ion-row>
                </div>
            </ion-accordion>
        </ion-accordion-group>
    </filter-base>
</template>

<script setup lang="ts">
import {
    IonAccordion,
    IonAccordionGroup,
    IonCheckbox,
    IonCol,
    IonIcon,
    IonItem,
    IonLabel,
    IonRadio,
    IonRadioGroup,
    IonRow,
    IonText
} from "@ionic/vue";
import { ref, computed, onMounted, reactive, watch } from "vue";
import { t } from "@/plugins/vue-i18n";

import FilterBase from "@/components/modal/FilterBase.vue";
import ButtonBase from "@/components/button/ButtonBase.vue";

import { useSupplierStore } from "@/stores/suppliers";
import { usePersistentStore } from "@/stores/persistent";
import { useListStore } from "@/stores/lists";

import { multiSelect } from "@/composables/viewFunctions/useList";

import { SORT_METHOD } from "@/types/enum/propEnum";
import { ION_VARIANT } from "@/types/enum/ionicEnum";
import { FilterInterfaceProps } from "@/types/props";
import { FilterInterfaceEmits } from "@/types/emits";

const props = defineProps<FilterInterfaceProps>()
const emit = defineEmits<FilterInterfaceEmits>()

const supplierStore = useSupplierStore()
const persistentStore = usePersistentStore()
const listStore = useListStore()

const sortOptions = reactive([
    { text: t('enumT.sortDirection.asc'), value: SORT_METHOD.ALPH_ASC },
    { text: t('enumT.sortDirection.desc'), value: SORT_METHOD.ALPH_DESC }
])

const filterValue = ref<string>('sort')

const catalogValues = computed<Record<string, boolean>>({
    get: () => {
        if (!props.tempCategory) return {}
        return props.tempCategory
    },
    set: (value) => {
        emit('update:tempCategory', value)
    }
})

const sortDirectionValue = computed<SORT_METHOD>({
    get: () => {
        return props.sortDirection
    },
    set: (value) => {
        emit('update:sortDirection', value)
    }
})
const tagValues = computed<Record<string, boolean>>({
    get: () => {
        if (!props.tempTags) return {}
        return props.tempTags
    },
    set: (value) => {
        emit('update:tempTags', value)
    }
})
const quantityModel = computed<boolean>({
    get: () => {
        return props.withQuantity ?? false
    },
    set: (value) => {
        emit('update:withQuantity', value)
    }
})

const switchToManualSorting = (): void => {
    sortDirectionValue.value = SORT_METHOD.MANUAL
    multiSelect.value = true
    emit('apply')
}

const changeAccordion = (value: string): void => {
    filterValue.value = filterValue.value === value ? '' : value
}

onMounted(() => {
    if (props.isList) sortOptions.push(...[
        { text: t('enumT.sortDirection.catSupplier'), value: SORT_METHOD.CAT_SUPPLIER },
        { text: t('enumT.sortDirection.catFilter'), value: SORT_METHOD.CAT_FILTER },
        { text: t('enumT.sortDirection.manually'), value: SORT_METHOD.MANUAL }
    ])

    if (!sortOptions.find((option) => option.value === sortDirectionValue.value)) {
        sortDirectionValue.value = props.isList ? SORT_METHOD.MANUAL : SORT_METHOD.ALPH_ASC
    }
})
</script>

<style scoped>
.filter-checkbox {
    --checkbox-background: var(--ion-color-grey4);
    --checkbox-background-checked: var(--ion-color-grey4);
    --border-color: var(--ion-color-grey4);
    --border-color-checked: var(--ion-color-grey4);
    --color-checked: var(--ion-color-purple);
    --checkmark-color: var(--ion-color-purple);
    --color: var(--ion-color-grey1);
}

.radio-container {
    width: 20px;
    height: 20px;
}
</style>
